/**
 * AMAE – una versione per ordine.
 *
 * Per i prodotti con il tag "una-versione-per-ordine" (oggi Casanova) non
 * lascia aggiungere al carrello una variante se nel carrello c'e' gia' una
 * variante DIVERSA dello stesso prodotto. Piu' copie della stessa variante
 * restano consentite. Vale in tutte e due le direzioni.
 *
 * DOVE AGISCE. Su ogni form di prodotto che porta l'attributo
 * data-amae-una-versione con l'ID del prodotto: lo scrive il Liquid
 * (snippet amae-una-versione) in blocks/buy-buttons.liquid — pagina
 * prodotto, finestra dell'aggiunta rapida, barra fissa — e in
 * snippets/quick-add.liquid. Il tag lo sa solo il Liquid: /cart.js non
 * restituisce i tag.
 *
 * COME. Horizon gestisce clic e invii con un unico ascoltatore sul
 * documento, in fase di cattura. Questo script ascolta sulla FINESTRA, in
 * cattura: nel percorso di un evento la finestra viene prima del documento,
 * quindi il controllo arriva prima di Horizon. Intercetta due cose:
 *   - il CLIC sul pulsante di aggiunta, perche' e' sul clic che Horizon
 *     fa partire l'animazione "aggiunto" (la scatola che vola nel carrello):
 *     bloccando solo l'invio, il cliente vedrebbe il successo e poi l'errore;
 *   - l'INVIO del form, per i casi senza clic (Invio in un campo).
 * Ferma l'evento, legge /cart.js e, se c'e' il conflitto, apre un popup con
 * il messaggio e il pulsante "Vai al carrello" (vedi sotto). Se non c'e',
 * ripete il clic (o l'invio) lasciandolo passare, e da li' e' tutto di
 * Horizon come sempre.
 *
 * Se il carrello non si legge (rete assente), non blocca: resta il
 * controllo nel carrello, che disattiva il pagamento.
 *
 * "Compra subito" non passa di qui: non e' un invio del form, e porta al
 * pagamento solo il prodotto scelto.
 */
(() => {
  if (window.__amaeUnaVersione) return;
  window.__amaeUnaVersione = true;

  /* I valori scritti dal Liquid si leggono sempre ripuliti: un a capo in
     fondo all'ID del prodotto basta a far fallire il confronto con il
     carrello, e allora il blocco non scatta mai. */

  /** @param {HTMLFormElement} form */
  const prodottoDi = (form) => (form.dataset.amaeUnaVersione ?? '').trim();

  /** @param {HTMLFormElement} form */
  const messaggioDi = (form) => (form.dataset.amaeMessaggio ?? '').trim();

  /** Un form di prodotto con il tag: l'unico che questo script controlla. */
  const protetto = (form) => form instanceof HTMLFormElement && prodottoDi(form) !== '';

  /** Il form a cui e' appena stato dato il via: il secondo giro passa. */
  let lasciaPassare = null;

  /** @param {HTMLFormElement} form */
  const variante = (form) => String(new FormData(form).get('id') ?? '').trim();

  const leggiCarrello = async () => {
    const base = window.Theme?.routes?.cart_url || '/cart';
    const risposta = await fetch(`${base}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!risposta.ok) throw new Error('Carrello non leggibile');
    return risposta.json();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {Promise<boolean>} true se l'aggiunta puo' passare
   */
  const puoPassare = async (form) => {
    const prodotto = prodottoDi(form);
    const idVariante = variante(form);
    try {
      const carrello = await leggiCarrello();
      return !carrello.items.some(
        (riga) => String(riga.product_id) === prodotto && String(riga.variant_id) !== idVariante
      );
    } catch {
      return true;
    }
  };

  /** @param {HTMLFormElement} form */
  const componenteDi = (form) => form.closest('product-form-component');

  /* ---------- Il popup ----------

     Un <dialog> nativo, aperto con showModal(): il browser porta il fuoco
     dentro e lo riporta al pulsante di aggiunta alla chiusura, blocca la
     pagina sotto, chiude con Esc, e i lettori di schermo lo annunciano come
     finestra con il suo titolo e il suo testo. Si crea una volta sola, alla
     prima occasione, e si riusa.

     Il testo del messaggio arriva dal form (lo scrive il Liquid, snippet
     amae-una-versione, lo stesso dell'avviso nel carrello). Titolo e
     pulsanti stanno qui: il sito e' solo in italiano. */
  const TITOLO = 'Nel carrello c’è già un’altra versione';
  const VAI_AL_CARRELLO = 'Vai al carrello';
  const HO_CAPITO = 'Ho capito';

  /** @type {HTMLDialogElement | null} */
  let popup = null;

  const creaPopup = () => {
    const dialogo = document.createElement('dialog');
    dialogo.className = 'amae-una-versione-popup';
    dialogo.setAttribute('aria-labelledby', 'amae-una-versione-titolo');
    dialogo.setAttribute('aria-describedby', 'amae-una-versione-messaggio');

    const corpo = document.createElement('form');
    corpo.method = 'dialog';
    corpo.className = 'amae-una-versione-popup__corpo';

    const titolo = document.createElement('h2');
    titolo.id = 'amae-una-versione-titolo';
    titolo.className = 'amae-una-versione-popup__titolo';
    titolo.textContent = TITOLO;

    const messaggio = document.createElement('p');
    messaggio.id = 'amae-una-versione-messaggio';
    messaggio.className = 'amae-una-versione-popup__testo';

    const azioni = document.createElement('div');
    azioni.className = 'amae-una-versione-popup__azioni';

    const carrello = document.createElement('a');
    carrello.className = 'amae-una-versione-popup__carrello';
    carrello.href = window.Theme?.routes?.cart_url || '/cart';
    carrello.textContent = VAI_AL_CARRELLO;
    carrello.autofocus = true;

    const chiudi = document.createElement('button');
    chiudi.type = 'submit';
    chiudi.className = 'amae-una-versione-popup__chiudi';
    chiudi.textContent = HO_CAPITO;

    azioni.append(carrello, chiudi);
    corpo.append(titolo, messaggio, azioni);
    dialogo.append(corpo);

    // Un clic sullo sfondo scuro, fuori dal riquadro, chiude.
    dialogo.addEventListener('click', (evento) => {
      if (evento.target === dialogo) dialogo.close();
    });

    document.body.append(dialogo);
    return dialogo;
  };

  /**
   * Apre il popup con il messaggio. Se il browser non conosce <dialog>
   * (molto vecchio), restituisce false e il messaggio va nella pagina.
   * @param {string} testo
   */
  const apriPopup = (testo) => {
    if (typeof HTMLDialogElement !== 'function') return false;
    popup ??= creaPopup();
    const messaggio = popup.querySelector('.amae-una-versione-popup__testo');
    if (messaggio) messaggio.textContent = testo;
    if (!popup.open) popup.showModal();
    return true;
  };

  /** @param {HTMLFormElement} form */
  const mostra = (form) => {
    const testo = messaggioDi(form);
    if (apriPopup(testo)) return;
    mostraNellaPagina(form, testo);
  };

  /**
   * Il messaggio dentro la pagina: e' il ripiego se il popup non si puo'
   * aprire.
   * @param {HTMLFormElement} form
   * @param {string} testo
   */
  const mostraNellaPagina = (form, testo) => {
    const componente = componenteDi(form);
    const areaErrore = componente?.querySelector('.product-form-text__error');
    const regioneLive = componente?.querySelector('[ref="liveRegion"]');

    if (areaErrore) {
      let riga = areaErrore.querySelector('.amae-una-versione-testo');
      if (!riga) {
        riga = document.createElement('span');
        riga.className = 'amae-una-versione-testo';
        areaErrore.append(riga);
      }
      riga.textContent = testo;
      areaErrore.classList.remove('hidden');
    } else {
      // Un form senza l'area errori di Horizon: un avviso subito dopo.
      let avviso = form.parentElement?.querySelector(':scope > .amae-una-versione-avviso');
      if (!avviso) {
        avviso = document.createElement('p');
        avviso.className = 'amae-una-versione-avviso';
        avviso.setAttribute('role', 'alert');
        form.after(avviso);
      }
      avviso.textContent = testo;
    }

    // La regione live di Horizon (aria-live="assertive"): svuotata e
    // riscritta, cosi' il messaggio viene riletto anche al secondo tentativo.
    if (regioneLive) {
      regioneLive.textContent = '';
      requestAnimationFrame(() => {
        regioneLive.textContent = testo;
      });
    }
  };

  /** @param {HTMLFormElement} form */
  const nascondi = (form) => {
    const componente = componenteDi(form);
    const riga = componente?.querySelector('.amae-una-versione-testo');
    if (riga) {
      riga.remove();
      const areaErrore = componente?.querySelector('.product-form-text__error');
      const altroTesto = areaErrore && [...areaErrore.childNodes].some(
        (nodo) => nodo.nodeType === Node.TEXT_NODE && nodo.textContent.trim() !== ''
      );
      if (areaErrore && !altroTesto) areaErrore.classList.add('hidden');
    }
    form.parentElement?.querySelector(':scope > .amae-una-versione-avviso')?.remove();

    // La regione live non deve conservare il messaggio dopo che e' sparito:
    // chi la raggiunge con il lettore di schermo sentirebbe un errore vecchio.
    const regioneLive = componente?.querySelector('[ref="liveRegion"]');
    if (regioneLive && regioneLive.textContent === messaggioDi(form)) {
      regioneLive.textContent = '';
    }
  };

  /**
   * Ferma l'evento, controlla il carrello e, se si puo', lo ripete.
   * @param {Event} evento
   * @param {HTMLFormElement} form
   * @param {() => void} ripeti
   */
  const intercetta = (evento, form, ripeti) => {
    evento.preventDefault();
    evento.stopImmediatePropagation();
    nascondi(form);
    puoPassare(form).then((ok) => {
      if (!ok) {
        mostra(form);
        return;
      }
      lasciaPassare = form;
      try {
        ripeti();
      } finally {
        lasciaPassare = null;
      }
    });
  };

  // 1. Il clic sul pulsante di aggiunta, prima dell'animazione di Horizon.
  window.addEventListener(
    'click',
    (evento) => {
      const bersaglio = evento.target instanceof Element ? evento.target : null;
      const bottone = bersaglio?.closest('button[type="submit"], input[type="submit"]');
      const form = /** @type {HTMLFormElement | null} */ (bottone?.form ?? null);
      if (!form || !protetto(form) || lasciaPassare === form) return;
      if (bottone.disabled) return;
      intercetta(evento, form, () => bottone.click());
    },
    true
  );

  // 2. L'invio del form senza clic sul pulsante.
  window.addEventListener(
    'submit',
    (evento) => {
      const form = evento.target;
      if (!protetto(form) || lasciaPassare === form) return;
      intercetta(evento, form, () => form.requestSubmit());
    },
    true
  );
})();
